import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Database } from '@/integrations/supabase/types';

type Sequence = Database['public']['Tables']['sequences']['Row'];
type Artist = Database['public']['Tables']['artists']['Row'];
type Genre = Database['public']['Tables']['genres']['Row'];

export interface SequenceWithMetadata extends Sequence {
  artist?: Artist;
  genre?: Genre;
}

// Valid sequence table columns based on current schema
const VALID_SEQUENCE_COLUMNS = [
  'id', 'name', 'title', 'description', 'duration', 'created_at', 'updated_at',
  'display_id', 'user_id', 'artist_id', 'genre_id', 'file_url', 'image_url', 
  'video_url', 'album_cover_url', 'spotify_track_id', 'spotify_explicit', 
  'spotify_preview_url', 'spotify_duration_ms', 'spotify_popularity', 'album',
  'price', 'download_count', 'featured', 'is_free', 'software_type',
  'claim_requested_at', 'claim_status', 'claim_verified_at', 'original_creator_info'
] as const;

// Helper function to safely filter sequence data to only include valid database columns
const filterSequenceData = (data: Partial<SequenceWithMetadata>): Partial<Sequence> => {
  const filtered: Partial<Sequence> = {};
  const invalidFields: string[] = [];
  
  for (const key in data) {
    if (VALID_SEQUENCE_COLUMNS.includes(key as any)) {
      const value = data[key as keyof typeof data];
      if (value !== undefined) {
        (filtered as any)[key] = value;
      }
    } else {
      // Log any invalid fields being filtered out
      invalidFields.push(key);
    }
  }
  
  // Explicitly remove any artist/genre fields that might have leaked through
  if ('artist' in (filtered as any)) {
    delete (filtered as any).artist;
    invalidFields.push('artist (explicitly removed)');
  }
  if ('genre' in (filtered as any)) {
    delete (filtered as any).genre;
    invalidFields.push('genre (explicitly removed)');
  }
  
  // Remove the ID field from updates to avoid conflicts
  if ('id' in (filtered as any)) {
    delete (filtered as any).id;
    invalidFields.push('id (removed from update data)');
  }
  
  // Log debugging information
  if (invalidFields.length > 0) {
    console.log('Filtered out invalid sequence fields:', invalidFields);
  }
  console.log('Valid sequence data being sent to Supabase:', filtered);
  
  // Validate that critical foreign key fields are UUIDs if present
  if (filtered.artist_id && typeof filtered.artist_id !== 'string') {
    console.error('Invalid artist_id type:', typeof filtered.artist_id, filtered.artist_id);
    delete filtered.artist_id;
  }
  if (filtered.genre_id && typeof filtered.genre_id !== 'string') {
    console.error('Invalid genre_id type:', typeof filtered.genre_id, filtered.genre_id);
    delete filtered.genre_id;
  }
  
  return filtered;
};

export function useAdminSequences() {
  const [sequences, setSequences] = useState<SequenceWithMetadata[]>([]);
  const [artists, setArtists] = useState<Artist[]>([]);
  const [genres, setGenres] = useState<Genre[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch sequences with their metadata
  const fetchSequences = async () => {
    try {
      setLoading(true);
      
      // First, get the total count
      const { count, error: countError } = await supabase
        .from('sequences')
        .select('*', { count: 'exact', head: true });
      
      if (countError) throw countError;
      
      const totalCount = count || 0;
      const pageSize = 1000; // Max allowed by Supabase
      const allSequences: any[] = [];
      
      // Fetch all sequences in batches without joins to avoid schema cache issues
      for (let offset = 0; offset < totalCount; offset += pageSize) {
        const { data, error } = await supabase
          .from('sequences')
          .select('*')
          .order('created_at', { ascending: false })
          .range(offset, offset + pageSize - 1);
        
        if (error) throw error;
        if (data) allSequences.push(...data);
      }
      
      // Fetch artists and genres separately and map them
      const { data: artistsData } = await supabase
        .from('artists')
        .select('*');
      
      const { data: genresData } = await supabase
        .from('genres')
        .select('*');
      
      // Map artist and genre data to sequences
      const sequencesWithMetadata = allSequences.map(sequence => ({
        ...sequence,
        artist: artistsData?.find(a => a.id === sequence.artist_id),
        genre: genresData?.find(g => g.id === sequence.genre_id)
      }));
      
      setSequences(sequencesWithMetadata);
    } catch (err) {
      console.error('Error fetching sequences:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch sequences');
    } finally {
      setLoading(false);
    }
  };

  // Fetch all artists
  const fetchArtists = async () => {
    try {
      const { data, error } = await supabase
        .from('artists')
        .select('*')
        .order('name');

      if (error) throw error;
      setArtists(data || []);
    } catch (err) {
      console.error('Error fetching artists:', err);
    }
  };

  // Fetch all genres
  const fetchGenres = async () => {
    try {
      const { data, error } = await supabase
        .from('genres')
        .select('*')
        .order('name');

      if (error) throw error;
      setGenres(data || []);
    } catch (err) {
      console.error('Error fetching genres:', err);
    }
  };

  // Create new artist
  const createArtist = async (name: string, description?: string, imageUrl?: string) => {
    try {
      const { data, error } = await supabase
        .from('artists')
        .insert([{
          name,
          description,
          image_url: imageUrl
        }])
        .select()
        .single();

      if (error) throw error;
      
      // Refresh artists list
      await fetchArtists();
      return data;
    } catch (err) {
      console.error('Error creating artist:', err);
      throw err;
    }
  };

  // Create new genre
  const createGenre = async (name: string, description?: string) => {
    try {
      const { data, error } = await supabase
        .from('genres')
        .insert([{
          name,
          description
        }])
        .select()
        .single();

      if (error) throw error;
      
      // Refresh genres list
      await fetchGenres();
      return data;
    } catch (err) {
      console.error('Error creating genre:', err);
      throw err;
    }
  };

  // Create new sequence
  const createSequence = async (sequence: Partial<SequenceWithMetadata>) => {
    try {
      // Use the safe filtering function to ensure only valid database columns are included
      const filteredSequence = filterSequenceData(sequence);

      console.log('Filtered sequence for Supabase create:', filteredSequence);

      // Use regular client for admin operations
      const { error } = await supabase
        .from('sequences')
        .insert([filteredSequence]);

      if (error) throw error;
      
      // Refresh sequences list to get the new sequence
      await fetchSequences();
      
      // Get the newly created sequence by fetching latest
      const { data: latestSequence } = await supabase
        .from('sequences')
        .select('*')
        .eq('name', sequence.name)
        .single();
      
      return latestSequence;
    } catch (err) {
      console.error('Error creating sequence:', err);
      throw err;
    }
  };

  // Update sequence
  const updateSequence = async (id: string, updates: Partial<SequenceWithMetadata>) => {
    try {
      // Validate that we have an ID
      if (!id) {
        throw new Error('Sequence ID is required for update');
      }

      // Debug: Check current authentication status
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError) {
        console.error('Auth error:', authError);
        throw new Error('Authentication failed');
      }
      if (!user) {
        throw new Error('User not authenticated');
      }
      console.log('Authenticated user:', user.email, 'ID:', user.id);

      // Use the safe filtering function to ensure only valid database columns are included
      const filteredUpdates = filterSequenceData(updates);
      
      // Explicitly set updated_at to ensure timestamp is updated
      filteredUpdates.updated_at = new Date().toISOString();

      console.log('Updating sequence:', id, 'with filtered data:', filteredUpdates);

      // Check if we have any valid updates after filtering
      if (Object.keys(filteredUpdates).length <= 1) { // Only updated_at
        throw new Error('No valid fields to update');
      }

      // Use regular client for admin operations - get the full response
      const { data, error } = await supabase
        .from('sequences')
        .update(filteredUpdates)
        .eq('id', id)
        .select(); // Make sure to select to get the updated data

      if (error) {
        console.error('Supabase update error:', error);
        throw error;
      }

      // Check if any rows were actually updated
      if (!data || data.length === 0) {
        throw new Error(`No sequence found with ID ${id} or update was blocked by permissions`);
      }

      console.log('Successfully updated sequence:', data[0].id);
      
      // Refresh sequences list to get updated data
      await fetchSequences();
      
      return data[0];
    } catch (err) {
      console.error('Error updating sequence:', err);
      throw err;
    }
  };

  // Upload file to sequences bucket
  const uploadSequenceFile = async (file: File, sequenceId: string) => {
    try {
      // Generate unique filename with timestamp to avoid conflicts
      const timestamp = Date.now();
      const fileExtension = file.name.split('.').pop();
      const fileName = `${sequenceId}_${timestamp}.${fileExtension}`;

      const { data, error } = await supabase.storage
        .from('sequences')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false // Don't overwrite existing files
        });

      if (error) throw error;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('sequences')
        .getPublicUrl(fileName);

      return {
        path: data.path,
        url: publicUrl
      };
    } catch (err) {
      console.error('Error uploading file:', err);
      throw err;
    }
  };

  // Find sequences missing metadata
  // Find potential duplicate sequences
  const findDuplicateSequences = async () => {
    try {
      const { data, error } = await supabase
        .from('sequences')
        .select('id, title, name, user_id, created_at, file_url')
        .order('title');

      if (error) throw error;

      const duplicateGroups: { [key: string]: typeof data } = {};
      
      data?.forEach(sequence => {
        const key = (sequence.title || sequence.name || '').toLowerCase().trim();
        if (!duplicateGroups[key]) {
          duplicateGroups[key] = [];
        }
        duplicateGroups[key].push(sequence);
      });

      // Filter to only groups with multiple entries
      const duplicates = Object.entries(duplicateGroups)
        .filter(([_, sequences]) => sequences.length > 1)
        .map(([title, sequences]) => ({ title, sequences }));

      return duplicates;
    } catch (err) {
      console.error('Error finding duplicate sequences:', err);
      return [];
    }
  };

  const findSequencesMissingMetadata = async () => {
    try {
      // First, get the total count
      const { count, error: countError } = await supabase
        .from('sequences')
        .select('*', { count: 'exact', head: true })
        .or('spotify_track_id.is.null,artist_id.is.null,genre_id.is.null,spotify_popularity.is.null');
      
      if (countError) throw countError;
      
      const totalCount = count || 0;
      const pageSize = 1000; // Max allowed by Supabase
      const allSequences: Sequence[] = [];
      
      // Fetch all sequences with missing metadata in batches
      for (let offset = 0; offset < totalCount; offset += pageSize) {
        const { data, error } = await supabase
          .from('sequences')
          .select('*')
          .or('spotify_track_id.is.null,artist_id.is.null,genre_id.is.null,spotify_popularity.is.null')
          .order('created_at', { ascending: false })
          .range(offset, offset + pageSize - 1);
        
        if (error) throw error;
        if (data) allSequences.push(...data);
      }
      
      return allSequences;
    } catch (err) {
      console.error('Error finding sequences missing metadata:', err);
      throw err;
    }
  };

  useEffect(() => {
    fetchSequences();
    fetchArtists();
    fetchGenres();
  }, []);

  return {
    sequences,
    artists,
    genres,
    loading,
    error,
    fetchSequences,
    fetchArtists,
    fetchGenres,
    createArtist,
    createGenre,
    createSequence,
    updateSequence,
    uploadSequenceFile,
    findSequencesMissingMetadata,
    findDuplicateSequences
  };
}